// frontend/src/components/InfoTooltip.tsx
import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type InfoTooltipProps = {
  content: React.ReactNode;
  className?: string;
  panelClassName?: string;
  panelTextClassName?: string;
  ariaLabel?: string;
  placement?: "right" | "left" | "top" | "bottom";
  size?: number;                  // SVG icon size (px) – visual size of the circle
  panelMaxWidth?: "sm" | "md" | "lg" | "xl";
};

export function InfoTooltip({
  content,
  className = "",
  panelClassName = "",
  panelTextClassName = "",
  ariaLabel,
  placement = "right",
  size = 16,                // smaller, per feedback
  panelMaxWidth = "xl",     // wider by default
}: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const tipId = useId();
  const { t } = useTranslation();

  const resolvedAriaLabel = ariaLabel ?? t("common.open_help");

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const targetNode = e.target as Node | null;
      if (!targetNode) return;
      if (panelRef.current?.contains(targetNode)) return;
      if (btnRef.current?.contains(targetNode)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const panelPos =
    placement === "right"
      ? "left-full ml-2 top-1/2 -translate-y-1/2"
      : placement === "left"
      ? "right-full mr-2 top-1/2 -translate-y-1/2"
      : placement === "top"
      ? "bottom-full mb-2 left-1/2 -translate-x-1/2"
      : "top-full mt-2 left-1/2 -translate-x-1/2";

  const maxW =
    panelMaxWidth === "xl"
      ? "max-w-[640px]"
      : panelMaxWidth === "lg"
      ? "max-w-[520px]"
      : panelMaxWidth === "md"
      ? "max-w-[420px]"
      : "max-w-[320px]";

  // Minimal trigger: no hover UI; focus ring only (brand orange)
  const triggerClasses = [
    "inline-flex items-center justify-center h-6 w-6 rounded-full",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6F0F]/60",
    "select-none",
    className,
  ].join(" ");

  return (
    <span className="relative inline-flex items-center">
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        aria-controls={tipId}
        aria-label={resolvedAriaLabel}
        onClick={() => setOpen((o) => !o)}
        className={triggerClasses}
      >
        {/* Established 'info' geometry (lucide/material-like):
           - Circle outline (black) with thicker stroke for clarity
           - 'i' stem and a separate dot using a tiny line segment to keep spacing at small sizes */}
        <svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          role="img"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="9" fill="none" stroke="#515151" strokeWidth="1.3" />
          <line x1="12" y1="11" x2="12" y2="16" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
          <line x1="12" y1="8" x2="12.01" y2="8" stroke="#515151" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <div
        ref={panelRef}
        id={tipId}
        role="tooltip"
        className={[
          "absolute z-20 rounded-lg border border-slate-200 bg-white p-3 shadow-xl",
          maxW,
          "w-[20rem]", // default width ~320px (can grow/shrink with content)
          "text-slate-700",
          panelPos,
          panelClassName,
          open ? "" : "hidden",
        ].join(" ")}
        data-state={open ? "open" : "closed"}
      >
        <div className={["leading-snug", panelTextClassName || "text-xs"].join(" ")}>{content}</div>
      </div>
    </span>
  );
}
